import { useState } from "react"
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { BellOff } from "lucide-react"
import { toast } from "sonner"
import { NotificationKey, ROLE_LABELS, type Me, type NotificationPreferences } from "@vienna/contracts"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Field, Heading, MiniAvatar, Section } from "@/components/workspace/primitives"
import { SaveBar } from "@/components/workspace/save-bar"
import { api, ApiError } from "@/lib/api"
import { notificationsQuery } from "@/lib/queries"
import { useMe, useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/settings/profile")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(notificationsQuery(params.workspace)),
  component: ProfileNotifications,
})

const SETTINGS: Array<{ key: NotificationKey; title: string; detail: string }> = [
  { key: "assigned", title: "A conversation is assigned to me", detail: "Know when a customer is waiting for your help." },
  { key: "mentioned", title: "A teammate mentions me", detail: "Keep up with internal notes that need your input." },
  { key: "channel_attention", title: "A channel needs attention", detail: "Find out when account access changes." },
  { key: "automation_intervention", title: "An automation needs intervention", detail: "Stay on top of blocked flows and uncertain sends." },
  { key: "campaign_complete", title: "A campaign completes", detail: "Get a summary when a campaign finishes." },
  { key: "moderation", title: "Moderation flags", detail: "A rule flagged a comment for the team." },
]

/** Screen 48. Preferences are per membership, so each workspace can notify differently. */
function ProfileNotifications() {
  const me = useMe()
  const { workspace, role, isOwner } = useWorkspaceContext()
  const queryClient = useQueryClient()
  const saved = useSuspenseQuery(notificationsQuery(workspace.slug)).data
  const [name, setName] = useState(me.name)
  const [prefs, setPrefs] = useState<NotificationPreferences>(saved)
  const [seen, setSeen] = useState(saved)
  if (saved !== seen) {
    setSeen(saved)
    setPrefs(saved)
  }

  const save = useMutation({
    mutationFn: async () => {
      if (name.trim() !== me.name) await api<Me>("/me", { method: "PATCH", body: JSON.stringify({ name: name.trim() }) })
      await api<NotificationPreferences>(`/w/${workspace.slug}/me/notifications`, { method: "PUT", body: JSON.stringify(prefs) })
    },
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: ["me"] }), queryClient.invalidateQueries({ queryKey: ["workspace", workspace.slug] })])
      toast.success("Preferences saved")
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Could not save preferences"),
  })

  const dirty = name.trim() !== me.name || NotificationKey.options.some((key) => prefs[key] !== saved[key])

  return (
    <>
      <Heading title="Profile & notifications" description="How you show up, and what gets your attention." />
      <div className="ws-profile-grid">
        <div>
          <Section title="Your profile">
            <div className="ws-person mb-6">
              <MiniAvatar name={name || me.name} size="size-12" />
              <div>
                <strong>{name || me.name}</strong>
                <small>
                  {ROLE_LABELS[role]}
                  {isOwner ? " · workspace owner" : ""}
                </small>
              </div>
            </div>
            <div className="ws-form-grid">
              <Field label="Display name">{(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} />}</Field>
              <Field label="Email address" hint="Your sign-in email. Contact support to change it.">
                {(id) => <Input id={id} value={me.email} type="email" readOnly />}
              </Field>
            </div>
          </Section>
          <Section title="Notify me when…" description="Choose the events that need your attention.">
            {SETTINGS.map(({ key, title, detail }) => (
              <div className="ws-toggle-row" key={key}>
                <div>
                  <Label htmlFor={`notification-${key}`}>{title}</Label>
                  <p>{detail}</p>
                </div>
                <Switch id={`notification-${key}`} checked={prefs[key] ?? false} onCheckedChange={(value) => setPrefs({ ...prefs, [key]: value })} />
              </div>
            ))}
          </Section>
          <SaveBar workspaceName={workspace.name} label="Save preferences" busy={save.isPending} disabled={!dirty || !name.trim()} onSave={() => save.mutate()} />
        </div>
        <aside className="ws-notification-list">
          <div className="ws-flex-between">
            <h2>Recent activity</h2>
          </div>
          <div className="ws-empty flex flex-col items-center gap-2 py-10 text-center">
            <BellOff size={18} className="text-muted-foreground" />
            <strong className="text-sm font-medium text-foreground">Nothing needs your attention</strong>
            <span>Assignments, mentions and channel alerts will appear here.</span>
          </div>
        </aside>
      </div>
    </>
  )
}
